import { type FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { portalLogin } from '@/api/portal';
import { usePortalAuthStore } from '@/stores/portalAuthStore';

/**
 * Client-portal login (story 128.4). External client persona (CLIENT_VIEWER):
 * email + password against POST /api/v1/portal/login. All failures collapse to
 * a single generic error (the backend is enumeration-safe). Standalone — no
 * AppShell, no staff chrome.
 */
export function PortalLoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setSession = usePortalAuthStore((s) => s.setSession);
  const alreadyIn = usePortalAuthStore((s) => s.isAuthenticated());

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (alreadyIn) navigate('/portal', { replace: true });
  }, [alreadyIn, navigate]);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const res = await portalLogin({ email, password });
      setSession({
        accessToken: res.accessToken,
        accessTokenExpiresAt: res.accessTokenExpiresAt,
        tenantId: res.tenantId,
        accountId: res.accountId,
      });
      navigate('/portal', { replace: true });
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 401) {
        setError(t('portal.login.errorInvalid'));
      } else {
        setError(t('portal.login.errorGeneric'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--color-lop-bg-deep)] p-4">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-sm rounded-[10px] border border-[var(--color-lop-border)] bg-[linear-gradient(135deg,var(--color-lop-bg-card),var(--color-lop-bg-mid))] p-8 shadow-[var(--shadow-lop-md)]"
      >
        <h1 className="mb-1 text-xl font-semibold text-[var(--color-lop-text-primary)]">
          {t('portal.login.title')}
        </h1>
        <p className="mb-6 text-sm text-[var(--color-lop-text-secondary)]">
          {t('portal.login.subtitle')}
        </p>

        <label className="mb-1 block text-sm text-[var(--color-lop-text-secondary)]" htmlFor="portal-email">
          {t('portal.login.email')}
        </label>
        <Input
          id="portal-email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="mb-4"
        />

        <label className="mb-1 block text-sm text-[var(--color-lop-text-secondary)]" htmlFor="portal-password">
          {t('portal.login.password')}
        </label>
        <Input
          id="portal-password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="mb-4"
        />

        {error && (
          <p className="mb-4 text-sm text-[var(--color-lop-danger)]" role="alert">
            {error}
          </p>
        )}

        <Button type="submit" disabled={submitting} className="w-full">
          {submitting ? t('portal.login.submitting') : t('portal.login.submit')}
        </Button>
      </form>
    </div>
  );
}
